import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, ArrowRight, Mail, Phone, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

interface FormData {
  application: string;
  applicationOther: string;
  flowGpm: string;
  solidsType: string;
  powerAvailable: string;
  waterSource: string;
  waterSourceOther: string;
  notes: string;
  name: string;
  email: string;
  phone: string;
}

interface Recommendation {
  primaryProduct: string;
  primaryLink: string;
  secondaryProduct?: string;
  secondaryLink?: string;
  meshGuidance: string;
  reasoning: string;
}

const SizeMyFilterPage = () => {
  const [formData, setFormData] = useState<FormData>({
    application: '',
    applicationOther: '',
    flowGpm: '',
    solidsType: '',
    powerAvailable: '',
    waterSource: '',
    waterSourceOther: '',
    notes: '',
    name: '',
    email: '',
    phone: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateRecommendation = (): Recommendation => {
    const isIntake = formData.application === 'Intake';
    const flowNum = parseInt(formData.flowGpm) || 0;

    if (isIntake) {
      if (formData.powerAvailable === 'Yes') {
        return {
          primaryProduct: 'SCS2 Self-Cleaning Intake Screen',
          primaryLink: '/filtration/suction-screens/scs2',
          secondaryProduct: flowNum > 100 ? 'BIG Series Inline Filter (downstream polishing)' : 'TIG Series Inline Filter (downstream polishing)',
          secondaryLink: flowNum > 100 ? '/filtration/inline/big' : '/filtration/inline/tig',
          meshGuidance: 'SCS2: 3/32" perforations for bulk debris | Inline polish: 80-120 mesh for emitter protection',
          reasoning: 'SCS2 screens with internal rotating spray bar provide automatic cleaning for intake applications with power available. Follow with inline filtration for complete two-stage protection—SCS2 removes bulk organic debris, inline filter provides final polishing for emitters.'
        };
      } else {
        return {
          primaryProduct: 'PSF Passive Fish-Friendly Screen',
          primaryLink: '/filtration/suction-screens/psf',
          secondaryProduct: flowNum > 100 ? 'BIG Series Inline Filter (downstream polishing)' : 'TIG Series Inline Filter (downstream polishing)',
          secondaryLink: flowNum > 100 ? '/filtration/inline/big' : '/filtration/inline/tig',
          meshGuidance: 'PSF: 3/32" perforations (≤0.4 fps approach velocity) | Inline polish: 80-120 mesh for emitter protection',
          reasoning: 'PSF passive screens meet fish protection requirements without power or moving parts. Ideal for remote sites. Follow with inline filtration for complete protection—PSF handles bulk debris, inline filter provides final polishing for emitters.'
        };
      }
    }

    if (formData.solidsType === 'Organic/Fibrous') {
      return {
        primaryProduct: 'BIG Series with Brush Cleaning (BIF/RAB)',
        primaryLink: '/filtration/inline/big/brush-cleaning',
        meshGuidance: '50-80 mesh for typical organic loading | 20-50 mesh for heavy fibrous debris',
        reasoning: 'Brush cleaning excels with organic and fibrous debris (algae, leaves, vegetation). Rotating nylon bristles sweep screen interior during cleaning cycles. Debris expelled through purge valve. Ideal for surface water sources with organic matter.'
      };
    }

    if (formData.solidsType === 'Sand/Silt') {
      return {
        primaryProduct: 'BIG Series with Vacuum Cleaning (MVC/AVC)',
        primaryLink: '/filtration/inline/big/vacuum-cleaning',
        meshGuidance: '80-120 mesh for sand and silt removal | 50-80 mesh for coarser granular solids',
        reasoning: 'Vacuum cleaning is optimal for sand, silt, and granular solids. Suction nozzle removes dense particles that settle on screen surface. More efficient than brush for heavy inorganic sediment. Common in well water and canal applications.'
      };
    }

    if (formData.solidsType === 'Mixed') {
      return {
        primaryProduct: 'BIG Series with Vacuum Cleaning (MVC/AVC)',
        primaryLink: '/filtration/inline/big/vacuum-cleaning',
        meshGuidance: '50-80 mesh for mixed debris loading',
        reasoning: 'Vacuum cleaning handles mixed debris types effectively. Works well with combination of organic matter and inorganic sediment. More versatile than brush for variable water quality. Consider upstream intake screen for heavy organic loading.'
      };
    }

    if (flowNum < 100) {
      return {
        primaryProduct: 'TIG Series Inline Filter',
        primaryLink: '/filtration/inline/tig',
        meshGuidance: '80 mesh standard for small flow applications',
        reasoning: 'TIG series provides compact true-inline filtration for flows under 100 GPM. Gasketed design with manual cleaning. Ideal for residential, small agricultural, or booster station applications where space is limited.'
      };
    }

    return {
      primaryProduct: 'BIG Series Inline Filter',
      primaryLink: '/filtration/inline/big',
      meshGuidance: '50-80 mesh for general agricultural use | 80-120 mesh for fine emitter protection',
      reasoning: 'BIG series modular platform with multiple cleaning options. Suitable for most agricultural and light industrial applications. Contact engineering to determine optimal cleaning method (brush, vacuum, or manual) based on your specific debris type and water source.'
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rec = generateRecommendation();
    setRecommendation(rec);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Size My Filter | CTC Waterworks"
        description="Get personalized filtration recommendations based on your flow rate, debris type, and application. Free sizing tool helps you select the right filter for drip irrigation, micro-sprinklers, pivots, and intake applications."
        canonical="https://www.ctcwaterworks.com/filtration/size-my-filter"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Size My Filter</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Settings className="text-ctc-blue mr-3" size={48} />
              <h1 className="text-5xl font-bold text-gray-900">
                Size My Filter
              </h1>
            </div>
            <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
              Get personalized filtration recommendations based on your specific application and water conditions.
            </p>

            {!submitted && (
              <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg text-left">
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-semibold text-gray-900">How it works:</span> Answer a few questions about your application, flow rate, and debris type. We'll recommend the best filtration solution for your needs. This tool provides general guidance—for detailed engineering and pricing, contact our team.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Recommendation Result */}
      {submitted && recommendation && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8 mb-8"
            >
              <div className="flex items-center mb-4">
                <CheckCircle className="text-green-600 mr-3" size={36} />
                <h2 className="text-3xl font-bold text-gray-900">Your Recommended Solution</h2>
              </div>

              <div className="space-y-6">
                {/* Primary Recommendation */}
                <div className="bg-white rounded-lg p-6 border border-green-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Primary Recommendation</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-ctc-blue mb-2">{recommendation.primaryProduct}</p>
                      <p className="text-gray-700 leading-relaxed mb-4">{recommendation.reasoning}</p>
                    </div>
                  </div>
                  <Link
                    to={recommendation.primaryLink}
                    className="inline-flex items-center bg-ctc-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    View Product Details
                    <ArrowRight className="ml-2" size={20} />
                  </Link>
                </div>

                {/* Secondary Recommendation */}
                {recommendation.secondaryProduct && (
                  <div className="bg-white rounded-lg p-6 border border-blue-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Two-Stage Protection</h3>
                    <p className="text-lg font-semibold text-gray-900 mb-2">{recommendation.secondaryProduct}</p>
                    <p className="text-gray-700 mb-4">
                      For complete system protection, add downstream inline filtration. This two-stage approach extends maintenance intervals and provides superior emitter protection.
                    </p>
                    <Link
                      to={recommendation.secondaryLink!}
                      className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      View Secondary Filter
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                )}

                {/* Mesh Guidance */}
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg p-6 border border-orange-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Mesh/Perforation Guidance</h3>
                  <p className="text-gray-700 leading-relaxed">{recommendation.meshGuidance}</p>
                </div>
              </div>

              {/* Mesh Quick Guide */}
              <div className="mt-8 bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Mesh/Perforation Quick Reference</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">20-50 Mesh (Coarse)</p>
                    <p className="text-sm text-gray-700">Heavy organic debris, leaves, algae mats</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">50-80 Mesh (Medium)</p>
                    <p className="text-sm text-gray-700">General agricultural, mixed debris</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">80-120 Mesh (Fine)</p>
                    <p className="text-sm text-gray-700">Emitter protection, fine sand/silt</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">3/32" Perforations</p>
                    <p className="text-sm text-gray-700">Intake screens, fish protection, bulk debris</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact?topic=filtration"
                  className="flex-1 bg-green-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center inline-flex items-center justify-center"
                >
                  <Mail className="mr-2" size={20} />
                  Talk to a Filtration Specialist
                </Link>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setRecommendation(null);
                    setFormData({
                      application: '',
                      applicationOther: '',
                      flowGpm: '',
                      solidsType: '',
                      powerAvailable: '',
                      waterSource: '',
                      waterSourceOther: '',
                      notes: '',
                      name: '',
                      email: '',
                      phone: ''
                    });
                  }}
                  className="flex-1 bg-gray-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-center"
                >
                  Start Over
                </button>
              </div>

              <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-4 rounded-r-lg">
                <div className="flex items-start">
                  <AlertCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={20} />
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Note:</span> These recommendations are based on typical applications. Site-specific factors (water chemistry, temperature, debris variations, system pressure) may require adjustments. Contact engineering for detailed sizing, pricing, and custom configurations.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Sizing Form */}
      {!submitted && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Application */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Application Type <span className="text-red-600">*</span>
                </label>
                <select
                  name="application"
                  value={formData.application}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                >
                  <option value="">Select application...</option>
                  <option value="Drip">Drip Irrigation</option>
                  <option value="Micro">Micro-Sprinklers</option>
                  <option value="Pivot">Pivot/Sprinkler</option>
                  <option value="Intake">Intake/Suction Screen</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {formData.application === 'Other' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Please specify application
                  </label>
                  <input
                    type="text"
                    name="applicationOther"
                    value={formData.applicationOther}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    placeholder="e.g., Hydroponic, Industrial cooling, etc."
                  />
                </div>
              )}

              {/* Flow Rate */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Flow Rate (GPM) <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  name="flowGpm"
                  value={formData.flowGpm}
                  onChange={handleInputChange}
                  required
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                  placeholder="e.g., 250"
                />
                <p className="text-sm text-gray-600 mt-1">Enter your maximum flow rate in gallons per minute</p>
              </div>

              {/* Solids Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Debris/Solids Type <span className="text-red-600">*</span>
                </label>
                <select
                  name="solidsType"
                  value={formData.solidsType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                >
                  <option value="">Select debris type...</option>
                  <option value="Organic/Fibrous">Organic/Fibrous (algae, leaves, vegetation)</option>
                  <option value="Sand/Silt">Sand/Silt/Granular</option>
                  <option value="Mixed">Mixed (organic + inorganic)</option>
                  <option value="Unknown">Unknown/Not Sure</option>
                </select>
              </div>

              {/* Power Available */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Power/Controls Available On-Site? <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="powerAvailable"
                      value="Yes"
                      checked={formData.powerAvailable === 'Yes'}
                      onChange={handleInputChange}
                      required
                      className="mr-2"
                    />
                    <span className="text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="powerAvailable"
                      value="No"
                      checked={formData.powerAvailable === 'No'}
                      onChange={handleInputChange}
                      required
                      className="mr-2"
                    />
                    <span className="text-gray-700">No</span>
                  </label>
                </div>
                <p className="text-sm text-gray-600 mt-1">For automatic cleaning systems (brush/vacuum)</p>
              </div>

              {/* Water Source */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Water Source <span className="text-red-600">*</span>
                </label>
                <select
                  name="waterSource"
                  value={formData.waterSource}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                >
                  <option value="">Select water source...</option>
                  <option value="Well">Well</option>
                  <option value="Canal">Canal/Ditch</option>
                  <option value="River">River/Stream</option>
                  <option value="Pond/Sump">Pond/Sump/Reservoir</option>
                  <option value="Municipal">Municipal/Treated</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {formData.waterSource === 'Other' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Please specify water source
                  </label>
                  <input
                    type="text"
                    name="waterSourceOther"
                    value={formData.waterSourceOther}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    placeholder="e.g., Reclaimed water, desalination, etc."
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Additional Notes/Details
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                  placeholder="Any specific concerns, existing equipment, special requirements, etc."
                />
              </div>

              {/* Contact Information */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information (Optional)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Provide your contact info if you'd like us to follow up with detailed recommendations and pricing.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                      placeholder="(509) 555-1234"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  className="flex-1 bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg"
                >
                  Get My Recommendation
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Expert Guidance?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our filtration engineers can provide detailed sizing, custom configurations, and system design assistance. Contact us for personalized recommendations and pricing.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Contact Engineering
            </Link>

            <a
              href="tel:(509) 901-7476"
              className="bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Call (509) 901-7476
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SizeMyFilterPage;
